import React, { useState, useCallback, useEffect } from 'react';
import { Users, UserFormData, Warehouse, WarehouseFormData, UserRole, UsersViewMode, Role, Classes, ClassesFormData } from '../../types';
import { USER_ROLE_OPTIONS, INITIAL_USER_FORM_DATA, INITIAL_WAREHOUSE_FORM_DATA, RESTUSER, RESTWAREHOUSES, RESTPOSTUSER, RESTPOSTWAREHOUSE, RESTDELETEUSER, RESTDELETEWAREHOUSE, RESTUPDATEWAREHOUSE, RESTUPDATEUSER, INITIAL_CLASSES_FORM_DATA, RESTCLASSES, RESTUPDATECLASSES, RESTPOSTCLASSES, RESTDELETECLASSES } from '../../constants';
import Input from './Input';
import Button from './Button';
import Select from './Select';

// Icons
const UserIcon: React.FC<{className?: string}> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`w-5 h-5 ${className}`}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
  </svg>
);

const WarehouseIcon: React.FC<{className?: string}> = ({ className }) => (
 <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`w-5 h-5 ${className}`}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125V6.375c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v.001c0 .621.504 1.125 1.125 1.125z" />
  </svg>
);

const EditIcon: React.FC<{className?: string}> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`w-5 h-5 ${className}`}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
  </svg>
);

const DeleteIcon: React.FC<{className?: string}> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`w-5 h-5 ${className}`}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12.56 0c1.153 0 2.243.096 3.222.261m3.222.261L11 5.79M14.74 9l-.346 9m-4.788 0L9.26 9" />
  </svg>
);

interface GenericIntf{
  token:string;
}

const UsersAndWarehouses: React.FC<GenericIntf> = ({token}) => {
  const [viewMode, setViewMode] = useState<UsersViewMode>('users');
  
  const [users, setUsers] = useState<Users[]>([]);
  const [userFormData, setUserFormData] = useState<UserFormData>(INITIAL_USER_FORM_DATA);
  const [userFormErrors, setUserFormErrors] = useState<Partial<Record<keyof UserFormData, string>>>({});
  const [editingUserId, setEditingUserId] = useState<number | null>(null);

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [warehouseFormData, setWarehouseFormData] = useState<WarehouseFormData>(INITIAL_WAREHOUSE_FORM_DATA);
  const [warehouseFormErrors, setWarehouseFormErrors] = useState<Partial<Record<keyof WarehouseFormData, string>>>({});
  const [editingWarehouseId, setEditingWarehouseId] = useState<number | null>(null);

  const [classes, setClasses] = useState<Classes[]>([]);
  const [classesFormData, setClassesFormData] = useState<ClassesFormData>(INITIAL_CLASSES_FORM_DATA);
  const [classesFormErrors, setClassesFormErrors] = useState<Partial<Record<keyof ClassesFormData, string>>>({});
  const [editingClassesId, setEditingClassesId] = useState<number | null>(null);

  const handleClassesFormChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setClassesFormData(prev => ({ ...prev, [name]: value }));
    setClassesFormErrors(prev => ({ ...prev, [name]: undefined }));
  }, []);
  
  const handleUserFormChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setUserFormData(prev => ({ ...prev, [name]: value }));
    setUserFormErrors(prev => ({ ...prev, [name]: undefined }));
  }, []);

  const handleWarehouseFormChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setWarehouseFormData(prev => ({ ...prev, [name]: value }));
    setWarehouseFormErrors(prev => ({ ...prev, [name]: undefined }));
  }, []);

  const validateUserForm = useCallback((): boolean => {
    const errors: Partial<Record<keyof UserFormData, string>> = {};
    if (!userFormData.Name.trim()) errors.Name = "Il nome è obbligatorio.";
    if (!userFormData.Surname.trim()) errors.Surname = "Il cognome è obbligatorio.";
    if (!userFormData.passwordInput.trim()) errors.passwordInput = "La password è obbligatoria.";
    else if (userFormData.passwordInput.length < 6) errors.passwordInput = "La password deve essere di almeno 6 caratteri.";
    if (!userFormData.role) errors.role = "Il ruolo è obbligatorio.";
    setUserFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [userFormData]);


  /*const postuser= async ( user:Users )=>{
    try {
      const response = await RESTPOSTUSER(user,token);
      console.log("Response:",response)
    } catch (error) {
      console.error(error);
    }
  };*/

  const handleSaveUser = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!validateUserForm()) return;

    if (editingUserId) {
      // Update existing user
      // Note: In a real app, password update would involve hashing.
      // Here, we just update the fields. If passwordInput is empty, we don't change the password.
      // Since we don't store the password itself in the User object, we only care if a new one is provided.
      
      const editUser: Users = {
        id: editingUserId,
        name: userFormData.Name,
        surname: userFormData.Surname,
        email: userFormData.Email,
        password: userFormData.passwordInput,
        role: userFormData.role===UserRole.Administrator?Role.ADMIN:Role.GUEST,
        // Password is not stored directly on the user object
      };

      const putuser= async ( user:Users )=>{
        const response = await RESTUPDATEUSER(user,token);
      }

      putuser(editUser);
      
      setUsers(prevUsers => prevUsers.map(user => 
        user.id === editingUserId 
        ? { ...user, 
            email: userFormData.Email, 
            name: userFormData.Name, 
            password: userFormData.passwordInput || user.password,
            surname: userFormData.Surname, 
            role: userFormData.role===UserRole.Administrator?Role.ADMIN:Role.GUEST,
          } 
        : user
      ));
       
      setEditingUserId(null);
    } else {
      // Add new user
      const newUser: Users = {
        id: undefined,
        name: userFormData.Name,
        surname: userFormData.Surname,
        email: userFormData.Email,
        password: userFormData.passwordInput,
        role: userFormData.role===UserRole.Administrator?Role.ADMIN:Role.GUEST,
        // Password is not stored directly on the user object
      };
      setUsers(prevUsers => [newUser, ...prevUsers]);
      const postuser= async ( user:Users )=>{
        const response = await RESTPOSTUSER(user,token);
        console.log("Response:",response);
      }
      postuser(newUser);
    }
    setUserFormData(INITIAL_USER_FORM_DATA);
    setUserFormErrors({});
  }, [token, userFormData, validateUserForm, editingUserId]);

  const handleEditUser = useCallback((userToEdit: Users) => {
    setEditingUserId(userToEdit.id!);
    setUserFormData({
      Email: userToEdit.email,
      Name: userToEdit.name,
      Surname: userToEdit.surname,
      passwordInput: '', // Clear password field for editing
      role: userToEdit.role===Role.ADMIN?UserRole.Administrator:UserRole.User,
    });
    setUserFormErrors({});
    setViewMode('users'); // Ensure user form is visible
  }, []);

  const handleDeleteUser = useCallback((userIdToDelete: number) => {
    if (window.confirm("Sei sicuro di voler eliminare questo utente?")) {
      const deluser= async ( userid:number )=>{
          const response = await RESTDELETEUSER(userid,token);
          //console.log("Response:",response);
        }
      deluser(userIdToDelete);
      setUsers(prevUsers => prevUsers.filter(user => user.id !== userIdToDelete));
      if (editingUserId === userIdToDelete) { // If deleting the user currently being edited
        setEditingUserId(null);
        setUserFormData(INITIAL_USER_FORM_DATA);
        setUserFormErrors({});
      }
    }
  }, [editingUserId, token]);
  
  const handleCancelEditUser = useCallback(() => {
    setEditingUserId(null);
    setUserFormData(INITIAL_USER_FORM_DATA);
    setUserFormErrors({});
  }, []);

  /* only add 
  const handleAddUser = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!validateUserForm()) return;

    const newUser: Users = {
      id: undefined,
      name: userFormData.Name,
      surname: userFormData.Surname,
      email: userFormData.Email,
      password: userFormData.passwordInput,
      role: userFormData.role===UserRole.Administrator?Role.ADMIN:Role.GUEST,
    };
    setUsers(prevUsers => [newUser, ...prevUsers]);
    setUserFormData(INITIAL_USER_FORM_DATA);
    const postuser= async ( user:Users )=>{
      const response = await RESTPOSTUSER(user,token);
      console.log("Response:",response);
    }
    postuser(newUser);
    setUserFormErrors({}); // Clear errors on successful submission
  }, [token,userFormData, validateUserForm]);
*/

 
  
  const validateWarehouseForm = useCallback((): boolean => {
    const errors: Partial<Record<keyof WarehouseFormData, string>> = {};
    if (!warehouseFormData.name.trim()) errors.name = "Il nome del magazzino è obbligatorio.";
    if (!warehouseFormData.location.trim()) errors.location = "La posizione è obbligatoria.";
    setWarehouseFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [warehouseFormData]);

  const handleSaveWarehouse = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if(!validateWarehouseForm()) return;

    if (editingWarehouseId) {
      // Update existing warehouse
      const editWarehouse: Warehouse = {
        id: editingWarehouseId,
        name: warehouseFormData.name,
        location: warehouseFormData.location,
      };
      const putwarehouse= async ( warehouse:Warehouse )=>{
        const response = await RESTUPDATEWAREHOUSE(warehouse,token);
        console.log("Response:",response);
      }
      putwarehouse(editWarehouse);
      setWarehouses(prevWarehouses => prevWarehouses.map(wh =>
        wh.id === editingWarehouseId
        ? { ...wh, id: wh.id, name: warehouseFormData.name, location: warehouseFormData.location }: wh
      ));
      setEditingWarehouseId(null);
      
    } else {
      // Add new warehouse
      const newWarehouse: Warehouse = {
        id: undefined,
        name: warehouseFormData.name,
        location: warehouseFormData.location,
      };
      setWarehouses(prevWarehouses => [newWarehouse, ...prevWarehouses]);
      const postwarehouse= async ( warehouse:Warehouse )=>{
        const response = await RESTPOSTWAREHOUSE(warehouse,token);
        //console.log("Response:",response);
      }
      postwarehouse(newWarehouse);
    }
    setWarehouseFormData(INITIAL_WAREHOUSE_FORM_DATA);
    setWarehouseFormErrors({});
  }, [token,warehouseFormData, validateWarehouseForm, editingWarehouseId]);

  const handleEditWarehouse = useCallback((warehouseToEdit: Warehouse) => {
    setEditingWarehouseId(warehouseToEdit.id!);
    setWarehouseFormData({
      name: warehouseToEdit.name,
      location: warehouseToEdit.location?warehouseToEdit.location:'',
    });
    setWarehouseFormErrors({});
    setViewMode('warehouses'); // Ensure warehouse form is visible
  }, []);

  const handleDeleteWarehouse = useCallback((warehouseIdToDelete: number) => {
    if (window.confirm("Sei sicuro di voler eliminare questo magazzino?")) {
      const delwarehouse = async ( warehouseid:number )=>{
          const response = await RESTDELETEWAREHOUSE(warehouseid,token);
          console.log("Response:",response);
        }
      delwarehouse(warehouseIdToDelete);
      setWarehouses(prevWarehouses => prevWarehouses.filter(wh => wh.id !== warehouseIdToDelete));
      if (editingWarehouseId === warehouseIdToDelete) { // If deleting the warehouse currently being edited
        setEditingWarehouseId(null);
        setWarehouseFormData(INITIAL_WAREHOUSE_FORM_DATA);
        setWarehouseFormErrors({});
      }
    }
  }, [editingWarehouseId, token]);

   // Effect to scroll to form when editing
  /*useEffect(() => {
    if (editingUserId || editingWarehouseId) {
      const formElement = document.getElementById(editingUserId ? 'user-form-card' : 'warehouse-form-card');
      formElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [editingUserId, editingWarehouseId]);*/

  const handleCancelEditWarehouse = useCallback(() => {
    setEditingWarehouseId(null);
    setWarehouseFormData(INITIAL_WAREHOUSE_FORM_DATA);
    setWarehouseFormErrors({});
  }, []);

  /*const handleAddWarehouse = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if(!validateWarehouseForm()) return;

    const newWarehouse: Warehouse = {
      id: undefined,
      name: warehouseFormData.name,
      location: warehouseFormData.location,
     };
    setWarehouses(prevWarehouses => [newWarehouse, ...prevWarehouses]);
    setWarehouseFormData(INITIAL_WAREHOUSE_FORM_DATA);
    const postwarehouse= async ( warehouse:Warehouse )=>{
      const response = await RESTPOSTWAREHOUSE(warehouse,token);
      console.log("Response:",response);
    }
    postwarehouse(newWarehouse);
    setWarehouseFormErrors({}); // Clear errors on successful submission
  }, [token,warehouseFormData, validateWarehouseForm]);*/


  const validateClassesForm = useCallback((): boolean => {
    const errors: Partial<Record<keyof ClassesFormData, string>> = {};
    if (!classesFormData.name.trim()) errors.name = "Il nome della classe è obbligatorio.";
    setClassesFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [classesFormData]);

  const handleSaveClasses = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if(!validateClassesForm()) return;

    if (editingClassesId) {
      // Update existing warehouse
      const editClasses: Classes = {
        id: editingClassesId,
        txt: classesFormData.name,
      };
      const putclasses= async ( classes:Classes )=>{
        const response = await RESTUPDATECLASSES(classes,token);
        console.log("Response:",response);
      }
      putclasses(editClasses);
      setClasses(prevClasses => prevClasses.map(wh =>
        wh.id === editingClassesId
        ? { ...wh, id: wh.id, name: classesFormData.name }: wh
      ));
      setEditingClassesId(null);
      
    } else {
      // Add new warehouse
      const newClasses: Classes = {
        id: undefined,
        txt: classesFormData.name,
      };
      setClasses(prevClasses => [newClasses, ...prevClasses]);
      const postclasses = async ( classes:Classes )=>{
        const response = await RESTPOSTCLASSES(classes,token);
        //console.log("Response:",response);
      }
      postclasses(newClasses);
    }
    setClassesFormData(INITIAL_CLASSES_FORM_DATA);
    setClassesFormErrors({});
  }, [classesFormData.name, editingClassesId, token, validateClassesForm]);

  const handleEditClasses = useCallback((classesToEdit: Classes) => {
    setEditingClassesId(classesToEdit.id!);
    setClassesFormData({
      name: classesToEdit.txt,
    });
    setClassesFormErrors({});
    setViewMode('classes'); // Ensure warehouse form is visible
  }, []);

  const handleDeleteClasses = useCallback((classesIdToDelete: number) => {
    if (window.confirm("Sei sicuro di voler eliminare questo magazzino?")) {
      const delclasses = async ( classesid:number )=>{
          const response = await RESTDELETECLASSES(classesid,token);
          console.log("Response:",response);
        }
      delclasses(classesIdToDelete);
      setClasses(prevClasses => prevClasses.filter(wh => wh.id !== classesIdToDelete));
      if (editingClassesId === classesIdToDelete) { // If deleting the warehouse currently being edited
        setEditingClassesId(null);
        setClassesFormData(INITIAL_CLASSES_FORM_DATA);
        setClassesFormErrors({});
      }
    }
  }, [editingClassesId, token]);

   // Effect to scroll to form when editing
  useEffect(() => {
    let formElement = null;
    if (editingUserId) {
      formElement = document.getElementById('user-form-card');
    }
    if(editingClassesId){
      formElement = document.getElementById('classes-form-card');
    }
    if(editingWarehouseId){
      formElement = document.getElementById('warehouse-form-card');
    }
    formElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    
  }, [editingClassesId, editingUserId, editingWarehouseId]);

  const handleCancelEditClasses = useCallback(() => {
    setEditingClassesId(null);
    setClassesFormData(INITIAL_CLASSES_FORM_DATA);
    setClassesFormErrors({});
  }, []);

  useEffect(()=>{
    const fetchwarehouse = async()=>{
        const response = await RESTWAREHOUSES(token);
        setWarehouses(response);
    };
    const fetchuser = async()=>{
        const response = await RESTUSER(token);
        setUsers(response);
    };
    const fetchclasses = async()=>{
        const response = await RESTCLASSES(token);
        setClasses(response);
    };
    
    fetchwarehouse();
    fetchuser();
    fetchclasses();
  },[token])


  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-slate-900 text-sky-400 p-4 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <h1 className="text-2xl font-bold">Gestione </h1>
          <nav className="space-x-2">
            <Button
              variant={viewMode === 'users' ? 'secondary' : 'neutral'}
              onClick={() => {setViewMode('users'); handleCancelEditWarehouse(); handleCancelEditClasses()}}
              className={`!text-sm !py-2 ${viewMode === 'users' ? 'bg-secondary hover:bg-secondary-dark' : 'bg-primary-light hover:bg-indigo-400 '}`}
            >
              <UserIcon className="mr-2" /> Utenti
            </Button>
            <Button
              variant={viewMode === 'warehouses' ? 'secondary' : 'neutral'}
              onClick={() => {setViewMode('warehouses'); handleCancelEditUser(); handleCancelEditClasses() }}
              className={`!text-sm !py-2 ${viewMode === 'warehouses' ? 'bg-secondary hover:bg-secondary-dark' : 'bg-primary-light hover:bg-indigo-400 '}`}
            >
             <WarehouseIcon className="mr-2" /> Magazzini
            </Button>
             <Button
              variant={viewMode === 'classes' ? 'secondary' : 'neutral'}
              onClick={() => {setViewMode('classes'); handleCancelEditUser(); handleCancelEditWarehouse();}}
              className={`!text-sm !py-2 ${viewMode === 'classes' ? 'bg-secondary hover:bg-secondary-dark' : 'bg-primary-light hover:bg-indigo-400 '}`}
            >
             <WarehouseIcon className="mr-2" /> Classi
            </Button>
          </nav>
        </div>
      </header>

      <main className="container mx-auto p-4 sm:p-6 lg:p-8 flex-grow">
        {viewMode === 'users' && (
          <section>
            <h2 className="text-3xl font-semibold text-gray-800 mb-6 flex items-center">
              <UserIcon className="w-8 h-8 mr-3 text-primary" /> Gestione Utenti
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-xl shadow-2xl">
                <h3 className="text-xl font-medium text-gray-700 mb-4">{editingUserId ? 'Modifica Utente' : 'Aggiungi Nuovo Utente'}</h3>
                <form onSubmit={handleSaveUser}>
                  <Input
                    label="Nome"
                    id="Name"
                    name="Name"
                    value={userFormData.Name}
                    onChange={handleUserFormChange}
                    error={userFormErrors.Name}
                    autoComplete="given-name"
                  />
                  <Input
                    label="Cognome"
                    id="Surname"
                    name="Surname"
                    value={userFormData.Surname}
                    onChange={handleUserFormChange}
                    error={userFormErrors.Surname}
                    autoComplete="family-name"
                  />
                  <Input
                    label="Password"
                    id="passwordInput"
                    name="passwordInput"
                    type="password"
                    value={userFormData.passwordInput}
                    onChange={handleUserFormChange}
                    error={userFormErrors.passwordInput}
                    autoComplete="new-password"
                  />
                  <Select
                    label="Ruolo"
                    id="role"
                    name="role"
                    options={USER_ROLE_OPTIONS}
                    value={userFormData.role}
                    onChange={handleUserFormChange}
                    error={userFormErrors.role}
                    aria-required="true"
                  />
                   <div className="flex space-x-2 mt-4">
                    <Button type="submit" variant="primary" className="flex-grow">
                      {editingUserId ? 'Salva Modifiche' : 'Aggiungi Utente'}
                    </Button>
                    {editingUserId && (
                      <Button type="button" variant="neutral" onClick={handleCancelEditUser} className="flex-grow">
                        Annulla
                      </Button>
                    )}
                  </div>
                  {false && (<Button type="submit" variant="primary" className="w-full mt-2">
                    Aggiungi Utente
                  </Button>)}
                </form>
              </div>
              <div className="space-y-4">
                {users.length === 0 ? (
                  <p className="text-center text-gray-500 py-10 bg-white rounded-xl shadow-xl">Nessun utente aggiunto.</p>
                ) : (
                  users.map(user => (
                    <div key={user.id} className="bg-white p-4 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <UserIcon className="w-6 h-6 mr-3 text-primary-light" />
                          <h4 className="text-lg font-semibold text-primary">{user.name} {user.surname}</h4>
                        </div>
                       <div className="space-x-2">
                          <Button 
                            variant="neutral" 
                            onClick={() => handleEditUser(user)} 
                            className="!p-2 text-primary hover:bg-primary-light hover:text-white"
                            aria-label={`Modifica utente ${user.name} ${user.surname}`}
                          >
                            <EditIcon />
                          </Button>
                          <Button 
                            variant="neutral" 
                            onClick={() => handleDeleteUser(user.id!)} 
                            className="!p-2 text-red-500 hover:bg-red-500 hover:text-white"
                            aria-label={`Elimina utente ${user.name} ${user.surname}`}
                          >
                            <DeleteIcon />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 ml-9">Ruolo: <span className="font-medium text-gray-800">{user.role}</span></p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </section>
        )}

        {viewMode === 'warehouses' && (
          <section>
            <h2 className="text-3xl font-semibold text-gray-800 mb-6 flex items-center">
               <WarehouseIcon className="w-8 h-8 mr-3 text-secondary" /> Gestione Magazzini
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-xl shadow-2xl">
                <h3 className="text-xl font-medium text-gray-700 mb-4">{editingWarehouseId ? 'Modifica Magazzino' : 'Aggiungi Nuovo Magazzino'}</h3>
                <form onSubmit={handleSaveWarehouse}>
                  <Input
                    label="Nome Magazzino"
                    id="name"
                    name="name"
                    value={warehouseFormData.name}
                    onChange={handleWarehouseFormChange}
                    error={warehouseFormErrors.name}
                  />
                  <Input
                    label="Posizione"
                    id="location"
                    name="location"
                    value={warehouseFormData.location}
                    onChange={handleWarehouseFormChange}
                    error={warehouseFormErrors.location}
                    aria-required="true"
                  />
                   <div className="flex space-x-2 mt-4">
                    <Button type="submit" variant="primary" className="flex-grow">
                      {editingWarehouseId ? 'Salva Modifiche' : 'Aggiungi Magazzino'}
                    </Button>
                     {editingWarehouseId && (
                      <Button type="button" variant="neutral" onClick={handleCancelEditWarehouse} className="flex-grow">
                        Annulla
                      </Button>
                    )}
                    </div>
                </form>
              </div>
              <div className="space-y-4">
                {warehouses.length === 0 ? (
                  <p className="text-center text-gray-500 py-10 bg-white rounded-xl shadow-xl">Nessun magazzino aggiunto.</p>
                ) : (
                  warehouses.map(warehouse => (
                    <div key={warehouse.id} className="bg-white p-4 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
                       <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <WarehouseIcon className="w-6 h-6 mr-3 text-secondary-light" />
                          <h4 className="text-lg font-semibold text-secondary">{warehouse.name}</h4>
                        </div>
                        <div className="space-x-2">
                          <Button 
                            variant="neutral" 
                            onClick={() => handleEditWarehouse(warehouse)} 
                            className="!p-2 text-secondary hover:bg-secondary-light hover:text-white"
                            aria-label={`Modifica magazzino ${warehouse.name}`}
                           >
                            <EditIcon />
                          </Button>
                          <Button 
                            variant="neutral" 
                            onClick={() => handleDeleteWarehouse(warehouse.id!)} 
                            className="!p-2 text-red-500 hover:bg-red-500 hover:text-white"
                            aria-label={`Elimina magazzino ${warehouse.name}`}
                          >
                            <DeleteIcon />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 ml-9">Posizione: <span className="font-medium text-gray-800">{warehouse.location}</span></p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </section>
        )}
         {viewMode === 'classes' && (
          <section>
            <h2 className="text-3xl font-semibold text-gray-800 mb-6 flex items-center">
               <WarehouseIcon className="w-8 h-8 mr-3 text-secondary" /> Gestione Classi
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-xl shadow-2xl">
                <h3 className="text-xl font-medium text-gray-700 mb-4">{editingClassesId ? 'Modifica Classe' : 'Aggiungi Nuova Classe'}</h3>
                <form onSubmit={handleSaveClasses}>
                  <Input
                    label="Nome Classe"
                    id="name"
                    name="name"
                    value={classesFormData.name}
                    onChange={handleClassesFormChange}
                    error={classesFormErrors.name}
                    aria-required="true"
                  />
                   <div className="flex space-x-2 mt-4">
                    <Button type="submit" variant="primary" className="flex-grow">
                      {editingClassesId ? 'Salva Modifiche' : 'Aggiungi Classe'}
                    </Button>
                     {editingClassesId && (
                      <Button type="button" variant="neutral" onClick={handleCancelEditClasses} className="flex-grow">
                        Annulla
                      </Button>
                    )}
                    </div>
                </form>
              </div>
              <div className="space-y-4">
                {classes.length === 0 ? (
                  <p className="text-center text-gray-500 py-10 bg-white rounded-xl shadow-xl">Nessuna classe aggiunta.</p>
                ) : (
                  classes.map(classe => (
                    <div key={classe.id} className="bg-white p-4 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
                       <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <WarehouseIcon className="w-6 h-6 mr-3 text-secondary-light" />
                          <h4 className="text-lg font-semibold text-secondary">{classe.txt}</h4>
                        </div>
                        <div className="space-x-2">
                          <Button 
                            variant="neutral" 
                            onClick={() => handleEditClasses(classe)} 
                            className="!p-2 text-secondary hover:bg-secondary-light hover:text-white"
                            aria-label={`Modifica magazzino ${classe.txt}`}
                           >
                            <EditIcon />
                          </Button>
                          <Button 
                            variant="neutral" 
                            onClick={() => handleDeleteClasses(classe.id!)} 
                            className="!p-2 text-red-500 hover:bg-red-500 hover:text-white"
                            aria-label={`Elimina classe ${classe.txt}`}
                          >
                            <DeleteIcon />
                          </Button>
                        </div>
                      </div>
                      
                    </div>
                  ))
                )}
              </div>
            </div>
          </section>
        )}
      </main>
      
    </div>
  );
};

export default UsersAndWarehouses;